import { Injectable } from '@angular/core';
import { Store } from "@ngrx/store";
import { AppStateModel } from "../shared-store/app-state.model";
import { setAnimalId } from "../shared-store/selected-variables/selected-variables.actions";
import { selectPets } from "../shared-store/pet/pet.selectors";
import { selectAnimalId } from "../shared-store/selected-variables/selected-variables.selectors";
import { AuthService } from "../../../../petszel-shared/src/lib/services/auth.service";
import { AnimalResponse } from "../models/branding.interface";

export const petWeightUnits = {
  kg: 'Kg',
  lbs: 'Lbs'
} as const;

export type WeightUnitType = typeof petWeightUnits[keyof typeof petWeightUnits];

@Injectable({
  providedIn: 'root'
})
export class PetService {

  private petProfile: AnimalResponse | null = null;

  constructor(private store: Store<AppStateModel>,
              private authService: AuthService) {
  }

  /*
  * This Section is for the selected pet after regular Log In or Magic Linl
  * */

  setSelectedAnimalId(animalId: string, isMagicLink = false): void {
    const isLoggedIn = this.authService.getLatestIsLoggedInValue();

    if (isLoggedIn || isMagicLink) {
      this.store.dispatch(setAnimalId({animalId}));
    }
  }

  reHydrateSelectedAnimalId(): void {
    this.store.select(selectAnimalId).subscribe(animalId => {
      const sessionAnimalId = localStorage.getItem('selectedPetId');
      const isLoggedIn = this.authService.getLatestIsLoggedInValue();

      if (isLoggedIn) {
        if (!animalId && !sessionAnimalId) {
          this.selectLatestAdoption();
        } else if (!animalId && sessionAnimalId) {
          this.store.dispatch(setAnimalId({animalId: sessionAnimalId}));
        } else if (animalId && !sessionAnimalId) {
          localStorage.setItem('selectedPetId', animalId);
        }
      }
    });
  }

  private selectLatestAdoption():  void {
    this.store.select(selectPets).subscribe(pets => {
      if (pets.length > 0) {
        const selectedPet = pets[pets.length - 1];
        this.setSelectedAnimalId(selectedPet.id);
      }
    });
  }


  /*
  * This Section is the Pet Profile page data
  * */

  setPetProfileData(pet: AnimalResponse): void {
    this.petProfile = pet;
    sessionStorage.setItem('petProfile', JSON.stringify(pet));
  }

  getPetProfileData(): AnimalResponse | null {
    if (this.petProfile) {
      return this.petProfile;
    } else {
      const storedPet = sessionStorage.getItem('petProfile');
      if (storedPet) {
        this.setPetProfileData(JSON.parse(storedPet));
        return this.petProfile;
      }

      return null;
    }
  }

  getPetPhoto(pet: AnimalResponse): string {
    if (pet) {
      if (pet.photo) {
        return pet.photo;
      } else  if (pet.additionalPhotos && pet.additionalPhotos.length > 0) {
        return pet.additionalPhotos[pet.additionalPhotos.length - 1];
      } else if (pet.animalType === 'Dog') {
        return 'assets/new-images/default-avatar--dog.svg';
      } else if (pet.animalType === 'Cat') {
        return 'assets/new-images/default-avatar--cat.svg';
      } else {
        return 'assets/images/paw-icon.png';
      }
    }

    return '';
  }

  getPetWeightConverted(weight: number, unitFrom: WeightUnitType, unitTo: WeightUnitType): string {
    const kgToLb = 2.2046226218;
    const lbToKg = 0.45359237;

    if (unitFrom === petWeightUnits.kg && unitTo === petWeightUnits.kg) {
      return `${weight} Kg`;
    }

    if (unitFrom === petWeightUnits.lbs && unitTo === petWeightUnits.kg) {
      const kilograms = weight * lbToKg;
      return `${Number(kilograms.toFixed(2))} Kg`;
    }

    const totalPounds = unitFrom === petWeightUnits.kg ? weight * kgToLb : weight;
    let pounds = Math.floor(totalPounds);
    let ounces = Math.round((totalPounds - pounds) * 16);

    if (ounces === 16) {
      pounds += 1;
      ounces = 0;
    }

    return ounces === 0 ? `${pounds} Lbs` : `${pounds} Lbs ${ounces} Oz`;
  }
}
